import React, { useEffect } from 'react';

import { type FEmailMessageLightFragment, useEmailMessageLastEventQuery } from '@dashboard/generated/wp/platform';
import { getTimelineLogItemLabel } from '@dashboard/models/TimelineLogItem';
import { Chip, Skeleton } from '@mui/material';

type Props = {
  row: FEmailMessageLightFragment;
};

function isDateWithinSeconds(d: unknown, seconds: number) {
  let date: Date | null = null;
  if (typeof d === 'string') {
    date = new Date(d);
  } else if (d instanceof Date) {
    date = d;
  } else {
    return true;
  }
  const diff = Date.now() - date.getTime();
  return diff < seconds * 1000;
}

export default function LastEventColumn({ row }: Props) {
  const { data, refetch } = useEmailMessageLastEventQuery({
    variables: { id: row.id },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    const interval = setInterval(async () => {
      if (!data) {
        return;
      }
      const { lastEvent, createdAt } = data.EmailMessage;
      if (!lastEvent) {
        await refetch();
        return;
      }

      if (isDateWithinSeconds(createdAt, 30)) {
        await refetch();
        return;
      }

      clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [data, refetch]);

  const event = data?.EmailMessage.lastEvent;
  if (event === undefined) {
    return <Skeleton width={45} />;
  }
  if (event === null) {
    return renderChip('Pending');
  }
  return renderChip(getTimelineLogItemLabel(event));
}

function renderChip(label: string) {
  return <Chip variant="outlined" size="small" label={label} sx={{ borderRadius: 3, lineHeight: 1 }} />;
}
